import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  GrCostBreakdownUpdateManagedInAs400Failure,
  UpdateGrCostBreakdownManagedInAs400Mutation,
  UpdateGrCostBreakdownManagedInAs400MutationVariables,
} from "@/graphql/codegen/graphql"
import { UpdateGrCostBreakdownManagedInAs400Document } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateGrCostBreakdownCostSourceReturn {
  updateCostSource: (goodsReceiptId: string, isCostManagedInAs400: boolean) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateGrCostBreakdownCostSource = (): UseUpdateGrCostBreakdownCostSourceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    UpdateGrCostBreakdownManagedInAs400Mutation,
    UpdateGrCostBreakdownManagedInAs400MutationVariables
  >(UpdateGrCostBreakdownManagedInAs400Document)

  const updateCostSource = useCallback(
    async (goodsReceiptId: string, isCostManagedInAs400: boolean): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { goodsReceiptId, isCostManagedInAs400 } })
      const output = data?.grCostBreakdown.updateManagedInAS400
      const hasError = error || output?.__typename === "GrCostBreakdownUpdateManagedInAS400Failure"

      if (hasError) {
        const message = error?.message ?? (output as GrCostBreakdownUpdateManagedInAs400Failure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Cost breakdown cost source updated" })

      return true
    },
    [executeMutation, notify]
  )

  return { updateCostSource, isUpdating: fetching }
}
